import { t } from './i18n';

type ImageItem = {
  id?: string;
  type?: string;
  src?: string;
  /** Full-size original used for print output; `src` is the medium copy shown on canvas. */
  large?: string;
  thumb?: string;
  file?: string;
  file_name?: string;
  /** Intrinsic size of the source bitmap; preflight derives the print DPI from it. */
  sourcePixelWidth?: number;
  sourcePixelHeight?: number;
  [key: string]: unknown;
};

type ImageToolContext = {
  item: ImageItem;
  update: (patch: Record<string, unknown>) => void;
};

type Operation = { kind: 'upscale'; scale: number } | { kind: 'remove-bg' };

type ProcessedMedia = {
  name: string;
  url: string;
  path: string;
  width: number;
  height: number;
};

type VeyopFacade = {
  http?: { request<R>(path: string, options?: RequestInit): Promise<R> };
  tools?: {
    registerImageTool: (tool: {
      id: string;
      label: string;
      priority?: number;
      render: (ctx: ImageToolContext) => HTMLElement;
    }) => () => void;
  };
  designer?: {
    beginHistoryBatch?: () => void;
    endHistoryBatch?: (reason?: string) => void;
    updateItem?: (id: string, patch: Record<string, unknown>) => void;
    resolveAssetUrl?: (raw: string, options?: { proxy?: boolean }) => string;
  };
  ui?: { notify?: (type: 'success' | 'error' | 'info' | 'warning', message: string) => void };
};

const TOOLBAR_BTN_CLASS =
  'vptw:px-2! vptw:py-1! vptw:text-xs! vptw:rounded-xl! vptw:hover:bg-gray-100! vptw:transition! vptw:cursor-pointer! vptw:text-gray-600! vptw:flex! vptw:items-center! vptw:gap-1! vptw:shrink-0! vptw:p-0! vptw:border-0!';
const MENU_ITEM_CLASS =
  'vptw:flex! vptw:w-full! vptw:items-center! vptw:gap-2! vptw:px-3! vptw:py-2! vptw:text-xs! vptw:font-medium! vptw:text-left! vptw:text-gray-700! vptw:bg-transparent! vptw:border-0! vptw:rounded-lg! vptw:cursor-pointer! vptw:transition! vptw:hover:bg-gray-100! disabled:vptw:opacity-50! disabled:vptw:cursor-not-allowed!';
const ERROR_RESET_MS = 4000;

let registered = false;
let runtime: VeyopFacade | null = null;
let closeActiveMenu: (() => void) | null = null;

function isImageItem(item: unknown): item is ImageItem {
  const candidate = item as ImageItem | null;
  return Boolean(candidate) && candidate?.type === 'image' && typeof candidate.src === 'string' && candidate.src.length > 0;
}

type EnabledTools = {
  upscale: boolean;
  removeBg: boolean;
  maxInputPixels: number;
  /** Factors the configured service supports (Stability AI only does 4x). */
  upscaleScales: number[];
  /** Largest source the service upscales; 0 = no limit known. */
  upscaleMaxPixels: number;
  upscaleMaxSide: number;
  maxOutputPixels: number;
};
let enabledTools: EnabledTools | null = null;

/** Flags come from the server via #designer-config; anything unknown stays enabled. */
function readEnabledTools(): EnabledTools {
  if (enabledTools) return enabledTools;
  enabledTools = { upscale: true, removeBg: true, maxInputPixels: 0, upscaleScales: [2, 4], upscaleMaxPixels: 0, upscaleMaxSide: 0, maxOutputPixels: 0 };
  try {
    const parsed = JSON.parse(document.getElementById('designer-config')?.textContent || '{}') as {
      items?: { aiImageTools?: Partial<EnabledTools>; backgroundRemover?: { active?: boolean } };
    };
    const flags = parsed.items?.aiImageTools;
    if (flags && typeof flags === 'object') {
      const scales = Array.isArray(flags.upscaleScales) ? flags.upscaleScales.map(Number).filter((n) => n > 1) : [2, 4];
      enabledTools = {
        upscale: flags.upscale !== false && scales.length > 0,
        removeBg: flags.removeBg !== false,
        maxInputPixels: Number(flags.maxInputPixels) || 0,
        upscaleScales: scales,
        upscaleMaxPixels: Number(flags.upscaleMaxPixels) || 0,
        upscaleMaxSide: Number(flags.upscaleMaxSide) || 0,
        maxOutputPixels: Number(flags.maxOutputPixels) || 0,
      };
    }
    // Background Remover offers this as "Remove background (HD)" in its own menu.
    if (parsed.items?.backgroundRemover?.active) enabledTools.removeBg = false;
  } catch {
    // Unreadable config: keep everything enabled and let the server decide.
  }
  return enabledTools;
}

/** Scale down to the server's input limit (it rejects larger images to bound provider cost). */
async function fitToPixels(blob: Blob, maxPixels: number): Promise<Blob> {
  if (maxPixels <= 0 || typeof createImageBitmap !== 'function') return blob;
  const bitmap = await createImageBitmap(blob);
  try {
    const pixels = bitmap.width * bitmap.height;
    if (pixels <= maxPixels) return blob;
    const scale = Math.sqrt(maxPixels / pixels) * 0.999;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(bitmap.width * scale));
    canvas.height = Math.max(1, Math.floor(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob>((resolve) => canvas.toBlob((out) => resolve(out ?? blob), 'image/png'));
  } finally {
    bitmap.close();
  }
}

/**
 * The designer's CSRF pair: WordPress checks the `veyop_api_nonce` header, OpenCart and
 * PrestaShop the session token posted as `_token`.
 */
function designerCsrf(): { header: Record<string, string>; token: string } {
  let config: Record<string, unknown> = {};
  try {
    config = JSON.parse(document.getElementById('designer-config')?.textContent || '{}') as Record<string, unknown>;
  } catch {
    // No config: send nothing and let the server reject the request.
  }
  const nonce = String(config.apiNonce ?? '').trim();
  return { header: nonce ? { 'X-CSRF-Token': nonce } : {}, token: String(config.csrfToken ?? '').trim() };
}

/** Cross-origin sources (stock images, CDNs) go through the designer's image proxy so they stay readable. */
function readableUrl(src: string): string {
  if (/^(data|blob):/i.test(src)) return src;
  return runtime?.designer?.resolveAssetUrl?.(src, { proxy: true }) || src;
}

async function processImage(src: string, operation: Operation): Promise<ProcessedMedia> {
  const http = runtime?.http;
  if (!http) throw new Error(t('AI image tools are unavailable.'));

  const source = await fetch(readableUrl(src), { credentials: 'same-origin' }).catch(() => null);
  if (!source?.ok) throw new Error(t('Could not read the image.'));
  // Background removal keeps print quality up to the server's limit; upscaling a source that
  // is already over the limit is pointless, so the menu hides it and the server refuses it.
  const raw = await source.blob();
  const blob = operation.kind === 'remove-bg' ? await fitToPixels(raw, readEnabledTools().maxInputPixels) : raw;

  const form = new FormData();
  form.append('file', new File([blob], 'image', { type: blob.type }));
  if (operation.kind === 'upscale') form.append('scale', String(operation.scale));

  const csrf = designerCsrf();
  if (csrf.token) form.append('_token', csrf.token);

  const route = operation.kind === 'remove-bg' ? '/ai-image-tools/remove-bg' : '/ai-image-tools/upscale';
  return http.request<ProcessedMedia>(route, { method: 'POST', body: form, headers: csrf.header });
}

/**
 * Why an upscale factor cannot run on this layer, or '' when it can. Unknown source sizes are
 * left to the server.
 */
function upscaleBlocker(item: ImageItem, scale: number): string {
  const width = Number(item.sourcePixelWidth) || 0;
  const height = Number(item.sourcePixelHeight) || 0;
  if (!width || !height) return '';
  const tools = readEnabledTools();
  const pixels = width * height;
  const tooLarge = (tools.upscaleMaxPixels > 0 && pixels > tools.upscaleMaxPixels)
    || (tools.upscaleMaxSide > 0 && Math.max(width, height) > tools.upscaleMaxSide)
    || (tools.maxOutputPixels > 0 && pixels * scale * scale > tools.maxOutputPixels);
  return tooLarge ? t('This image already has enough resolution to upscale.') : '';
}

/**
 * Layer fields for a processed image. Every field that names the bitmap moves to the result
 * (the renderer prefers `medium` for cliparts, print output uses `large`), and the source size
 * follows so preflight recomputes the DPI. The crop is stored as fractions and the result keeps
 * the aspect ratio, so it stays valid.
 */
function resultPatch(result: ProcessedMedia): Record<string, unknown> {
  return {
    src: result.url,
    thumb: result.url,
    large: result.url,
    medium: undefined,
    medium_url: undefined,
    file: result.path,
    file_name: result.name,
    sourcePixelWidth: result.width,
    sourcePixelHeight: result.height,
  };
}

function commitItem(ctx: ImageToolContext, patch: Record<string, unknown>, reason: string): void {
  const host = runtime?.designer;
  // A single history batch makes the AI result undoable in one step.
  if (host?.updateItem && ctx.item.id) {
    host.beginHistoryBatch?.();
    host.updateItem(ctx.item.id, patch);
    host.endHistoryBatch?.(reason);
    return;
  }
  ctx.update(patch);
}

function makeMenuItem(label: string, title: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  button.title = title;
  button.className = MENU_ITEM_CLASS;
  button.setAttribute('role', 'menuitem');
  return button;
}

function positionMenu(menu: HTMLElement, trigger: HTMLElement): void {
  const rect = trigger.getBoundingClientRect();
  const menuWidth = menu.offsetWidth || 208;
  const left = Math.max(8, Math.min(Math.round(rect.left), window.innerWidth - menuWidth - 8));
  menu.style.top = `${Math.round(rect.bottom + 6)}px`;
  menu.style.left = `${left}px`;
}

function createAiImageTool(ctx: ImageToolContext): HTMLElement {
  const wrap = document.createElement('span');
  wrap.style.display = isImageItem(ctx.item) ? 'inline-flex' : 'none';
  wrap.style.alignItems = 'center';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.title = t('AI image tools');
  trigger.className = TOOLBAR_BTN_CLASS;
  trigger.style.height = '28px';
  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.textContent = t('AI Enhance');

  const menu = document.createElement('div');
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', t('AI image tools'));
  menu.style.position = 'fixed';
  menu.style.zIndex = '2147483600';
  menu.className =
    'vptw:min-w-[208px]! vptw:rounded-2xl! vptw:border! vptw:border-gray-100! vptw:bg-white! vptw:p-1.5! vptw:shadow-xl! vptw:ring-1! vptw:ring-black/5!';

  const enabled = readEnabledTools();
  const actions: Array<{ operation: Operation; button: HTMLButtonElement; reason: string; title: string }> = [];
  if (enabled.upscale) {
    for (const scale of enabled.upscaleScales) {
      const title = scale === 2 ? t('Double the image resolution with AI') : scale === 4 ? t('Quadruple the image resolution with AI') : t('Raise the image resolution with AI');
      const label = scale === 2 ? t('Upscale 2x') : scale === 4 ? t('Upscale 4x') : `${t('Upscale')} ${scale}x`;
      actions.push({ operation: { kind: 'upscale', scale }, button: makeMenuItem(label, title), reason: `ai-upscale-${scale}x`, title });
    }
  }
  if (enabled.removeBg) {
    const title = t('Remove the background with AI');
    actions.push({ operation: { kind: 'remove-bg' }, button: makeMenuItem(t('Remove background'), title), reason: 'ai-remove-bg', title });
  }
  if (actions.length === 0) wrap.style.display = 'none';

  /** Disable factors the service would refuse for this layer, with the reason as the tooltip. */
  const refreshAvailability = (): void => {
    for (const { operation, button, title } of actions) {
      const blocker = operation.kind === 'upscale' && isImageItem(ctx.item) ? upscaleBlocker(ctx.item, operation.scale) : '';
      button.disabled = busy || blocker !== '';
      button.title = blocker || title;
    }
  };

  let busy = false;
  const setBusy = (next: boolean): void => {
    busy = next;
    trigger.disabled = next;
    refreshAvailability();
    trigger.textContent = next ? t('Processing…') : t('AI Enhance');
    trigger.style.color = next ? 'var(--vs-accent, #007aff)' : '';
  };

  const run = async (operation: Operation, reason: string): Promise<void> => {
    if (busy || !isImageItem(ctx.item)) return;
    setBusy(true);
    try {
      // `large` is the full-size original: processing the medium `src` would cap print quality.
      const result = await processImage(String(ctx.item.large || ctx.item.src), operation);
      // The processed image keeps the item's aspect ratio, so its on-canvas size is
      // unchanged and an upscale simply raises the effective DPI.
      commitItem(ctx, resultPatch(result), reason);
      setBusy(false);
    } catch (error) {
      setBusy(false);
      const message = error instanceof Error && error.message ? error.message : t('AI processing failed.');
      // A tooltip alone is invisible on touch screens.
      runtime?.ui?.notify?.('error', message);
      trigger.textContent = t('Failed');
      trigger.title = message;
      window.setTimeout(() => {
        if (!busy) {
          trigger.textContent = t('AI Enhance');
          trigger.title = t('AI image tools');
        }
      }, ERROR_RESET_MS);
    }
  };

  for (const { operation, button, reason } of actions) {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeActiveMenu?.();
      void run(operation, reason);
    });
    menu.appendChild(button);
  }

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const wasOpen = menu.isConnected;
    closeActiveMenu?.();
    if (wasOpen) return;

    // Mount on <body> while open: the toolbar's backdrop-filter would otherwise
    // become the containing block for this position:fixed menu.
    refreshAvailability();
    document.body.appendChild(menu);
    positionMenu(menu, trigger);
    trigger.setAttribute('aria-expanded', 'true');
    actions.find(({ button }) => !button.disabled)?.button.focus();

    const onPointer = (e: Event): void => {
      const target = e.target as Node;
      if (!menu.contains(target) && !trigger.contains(target)) closeActiveMenu?.();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        closeActiveMenu?.();
        trigger.focus();
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const usable = actions.map(({ button }) => button).filter((button) => !button.disabled);
      if (!usable.length) return;
      e.preventDefault();
      const index = usable.indexOf(document.activeElement as HTMLButtonElement);
      const step = e.key === 'ArrowDown' ? 1 : -1;
      usable[(index + step + usable.length) % usable.length].focus();
    };
    const onReposition = (): void => positionMenu(menu, trigger);
    window.setTimeout(() => document.addEventListener('pointerdown', onPointer, true), 0);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    closeActiveMenu = () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      menu.remove();
      trigger.setAttribute('aria-expanded', 'false');
      closeActiveMenu = null;
    };
  });

  wrap.append(trigger);
  return wrap;
}

function register(Veyop: VeyopFacade): void {
  if (registered || !Veyop.tools?.registerImageTool) return;
  registered = true;
  runtime = Veyop;
  Veyop.tools.registerImageTool({
    id: 'ai-image-tools.enhance',
    label: t('AI Enhance'),
    priority: 11,
    render: createAiImageTool,
  });
}

function boot(): void {
  const start = Date.now();
  const tick = (): void => {
    const Veyop = (window as unknown as { Veyop?: VeyopFacade }).Veyop;
    if (Veyop) register(Veyop);
    if (registered || Date.now() - start > 5000) return;
    window.setTimeout(tick, 50);
  };
  tick();
}

boot();
